// Day boundaries in the household's timezone, shared so the phone and the Worker agree on what
// "today" means wherever either of them happens to run (Workers run in UTC).

const HOUSEHOLD_TIMEZONE = "Europe/London";

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

// Made once: building an Intl.DateTimeFormat is far slower than using one, and auto-close and
// the history call dayKey for every tap in a range.
const DAY_FORMAT = new Intl.DateTimeFormat("en-CA", {
  timeZone: HOUSEHOLD_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const WALL_CLOCK_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: HOUSEHOLD_TIMEZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});
const TIME_FORMAT = new Intl.DateTimeFormat("en-GB", {
  timeZone: HOUSEHOLD_TIMEZONE,
  hour: "2-digit",
  minute: "2-digit",
});

/** "YYYY-MM-DD" for a timestamp in the household timezone. */
export function dayKey(ts: number): string {
  return DAY_FORMAT.format(new Date(ts)); // en-CA yields YYYY-MM-DD
}

/** True for a real calendar date written as YYYY-MM-DD. */
export function isDayKey(s: unknown): s is string {
  if (typeof s !== "string" || !DAY_RE.test(s)) return false;
  const t = Date.parse(s + "T00:00:00Z");
  return !Number.isNaN(t) && new Date(t).toISOString().slice(0, 10) === s;
}

/** The key of the day after `day`. */
export function nextDay(day: string): string {
  return addDays(day, 1);
}

/** The key of the day `n` days after `day` (before it, for a negative `n`). */
export function addDays(day: string, n: number): string {
  // Day keys are calendar dates, so stepping them in UTC never meets a clock change.
  return new Date(Date.parse(day + "T00:00:00Z") + n * 86_400_000).toISOString().slice(0, 10);
}

/** Whole days from `a` to `b` (negative when `b` is earlier). */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(b + "T00:00:00Z") - Date.parse(a + "T00:00:00Z")) / 86_400_000);
}

/** How far the household's wall clock is ahead of UTC at instant `ts`, in ms (London: 0 or 1 h). */
function utcOffsetMs(ts: number): number {
  const parts = WALL_CLOCK_FORMAT.formatToParts(new Date(ts));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const wall = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  return wall - Math.floor(ts / 1000) * 1000;
}

/** The instant a day starts (local midnight) in the household timezone. */
export function startOfDay(day: string): number {
  const midnightUtc = Date.parse(day + "T00:00:00Z");
  const guess = midnightUtc - utcOffsetMs(midnightUtc);
  // Re-check at the guess itself, in case a clock change falls between the two instants.
  return midnightUtc - utcOffsetMs(guess);
}

/** The instant the household's wall clock reads `hour`:00 on `day`. */
export function atHour(day: string, hour: number): number {
  const wallUtc = Date.parse(day + "T00:00:00Z") + hour * 3_600_000;
  const guess = wallUtc - utcOffsetMs(wallUtc);
  return wallUtc - utcOffsetMs(guess);
}

/** The hour (0 to 23) on the household's wall clock at `ts`. */
export function hourOf(ts: number): number {
  const parts = WALL_CLOCK_FORMAT.formatToParts(new Date(ts));
  return Number(parts.find((p) => p.type === "hour")?.value);
}

/** A day as a [from, to) range of timestamps. 23 or 25 hours long on clock-change days. */
export function dayRange(day: string): { from: number; to: number } {
  return { from: startOfDay(day), to: startOfDay(nextDay(day)) };
}

/** "17:42" in the household timezone. */
export function timeOfDay(ts: number): string {
  return TIME_FORMAT.format(new Date(ts));
}

/** The Monday that starts the week holding `day` (weeks run Monday to Sunday, as in the UK). */
export function weekStart(day: string): string {
  // getUTCDay on the key's own midnight is that calendar date's weekday, 0 = Sunday.
  const weekday = new Date(day + "T00:00:00Z").getUTCDay();
  return addDays(day, -((weekday + 6) % 7));
}
